import { OrderDispatchOption, Period } from "~/types";
import { useLocation } from "./locations";
import { useBaseMenu } from "./menu-v2";
import { DateTime as LuxonDateTime } from "luxon";

type Status = "now open" | "closing soon" | "closed";

const HOURS_24 = 24 * 60 * 60 * 1000;

export interface HoursResult {
  from: Date;
  to: Date;
  status: Status;
  message: string | null;
  loading: boolean;
}

export function useHours(
  locationRef: string,
  type: "STORE" | OrderDispatchOption | null,
): HoursResult | null {
  const { location, loading: locationLoading } = useLocation(locationRef, {
    revalidateOnFocus: false,
  });

  // Jet's currently returns same serviceAvailability for PICK_UP and DELIVERY
  // Use "PICK_UP" to get 'store hours'
  const adjustedType = type === "STORE" ? "PICK_UP" : type;

  const { serviceAvailability, isLoading: menuLoading } = useBaseMenu(
    locationRef,
    adjustedType,
    null,
  );

  // manually set this to force specific time of day states
  // const now = new Date("2024-11-30 00:20:00");
  const now = new Date();

  const hours = getHours(serviceAvailability, location?.timezone, type, now);

  const loading = locationLoading || menuLoading;

  if (!hours) {
    if (!loading) {
      console.warn("No serviceAvailability for the current day");
    }
    return null;
  }

  return {
    loading,
    ...hours,
  };
}

export function getHours(
  serviceAvailability: Period[] | null | undefined,
  timezone: string | undefined,
  type: "STORE" | OrderDispatchOption | null,
  now: Date,
) {
  const result = nextAvailable(serviceAvailability, timezone, type, now);

  if (!result) {
    // always expected for Jet's, except while loading
    return null;
  }

  let message: string | null = null;
  let status: Status = "closed";
  const from = result.start;
  const to = result.lastOrder; // go to the last order time, may be before end time

  if (from <= now && now < to) {
    status = "now open";

    const minutesToClose = Math.round(
      (to.getTime() - now.getTime()) / 1000 / 60,
    );

    // show a warning within 60 mins of cutoff
    if (minutesToClose > 0 && minutesToClose <= 60) {
      status = "closing soon";
      switch (type) {
        case "STORE":
          message = `Store closing in ${minutesToClose} minutes`;
          break;
        case "DELIVERY":
          message = `${minutesToClose} minutes left to order DELIVERY for today`;
          break;
        case "PICK_UP":
          message = `${minutesToClose} minutes left to order PICK UP for today`;
          break;
        default:
          break;
      }
    }
  }

  return { status, message, from, to };
}

const weekdays = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
] as const;
type Weekday = (typeof weekdays)[number];

function getWeekdayNumber(day: Weekday) {
  // Match the Luxon weekday where 1 = Monday and 7 = Sunday
  return weekdays.indexOf(day) + 1;
}

/**
 * Returns the next available period.
 * If there is an active period, it will be returned.
 * If no active period is found, the next available period will be
 * returned if one is found today OR tomorrow ONLY.
 * Periods that touch will be merged.
 */
function nextAvailable(
  serviceAvailability: Period[] | null | undefined,
  timeZone?: string,
  type: "PICK_UP" | "DELIVERY" | "SERVICE" | "STORE" | null = null,
  now: Date = new Date(),
): { start: Date; end: Date; lastOrder: Date; period: Period } | null {
  /*
  pipeline:
  -> add real timestamps to periods
  -> sort by start time
  -> merge periods that touch over midnight
  -> filter out irrelevant periods
  -> return first on the resulting list if one is found
  */

  if (!serviceAvailability || !serviceAvailability.length) return null;

  const todayWeekdayNumber =
    LuxonDateTime.fromJSDate(now).setZone(timeZone).weekday;

  let offset = 0;
  // Dave asked for the offset to be changed from 15 back to 0.
  // Keeping this code incase it changes back.
  if (type === "PICK_UP") offset = 0;
  if (type === "DELIVERY") offset = 0;
  // Will be treated as closed when within the offset (15 mins) of closing time

  const sorted = serviceAvailability
    .map((period) => {
      const weekdayNumber = getWeekdayNumber(period.dayOfTheWeek);
      const startOfDay = LuxonDateTime.fromJSDate(now)
        .setZone(timeZone)
        .startOf("week")
        .plus({ days: weekdayNumber - 1 });

      let [hours, minutes] = period.start.split(":").map(Number);
      let start = startOfDay.set({ hour: hours, minute: minutes });

      [hours, minutes] = period.end.split(":").map(Number);
      let end = startOfDay.set({
        hour: hours,
        // Some Jet's stores send 02:00, some 01:59 :()
        minute: minutes === 0 || minutes === 30 ? minutes : minutes + 1,
      });

      // end before start -> meant to end following day
      if (end.toUnixInteger() < start.toUnixInteger()) {
        end = end.plus({ days: 1 });
      }

      // anything already passed for this week move to next
      if (
        start.toJSDate() < now &&
        end.toJSDate() <= now &&
        // keep the last 24h blocks where they are as they may be relevant to merge
        now.getTime() - end.toJSDate().getTime() > HOURS_24
      ) {
        start = start.plus({ weeks: 1 });
        end = end.plus({ weeks: 1 });
      }

      const startDate = start.toJSDate();
      const endDate = end.toJSDate();
      const lastOrder = new Date(endDate.getTime() - offset * 60 * 1000);
      const active = startDate <= now && now < lastOrder;

      return {
        start: startDate,
        end: endDate,
        lastOrder,
        active,
        period,
        _startTs: start,
        _endTs: end,
      };
    })
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const merged: {
    start: Date;
    end: Date;
    lastOrder: Date;
    period: Period;
    active: boolean;
    _startTs: LuxonDateTime;
    _endTs: LuxonDateTime;
  }[] = [];

  // the merging assumes the periods are sorted and that only 2 periods can touch
  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i];
    const result = { ...current, period: { ...current.period } };

    const next = sorted[i + 1];
    if (next && current.end >= next.start) {
      result.end = next.end;
      result._endTs = next._endTs;
      result.lastOrder = next.lastOrder;
      result.period.end = next.period.end;
      const active = result.start <= now && now < result.lastOrder;
      result.active = active;
      i++;
    }

    merged.push(result);
  }

  const filtered = merged.filter((r) => {
    if (r.active) return true;

    const nowTime = now.getTime();

    const diff = r.start.getTime() - nowTime;
    const weekdayStart = r._startTs.weekday;
    if (weekdayStart === todayWeekdayNumber) {
      // don't return for same weekday next week or if we've passed the last order time
      return diff < HOURS_24 && r.lastOrder > now;
    }

    // Return true for tomorrow only
    // Wed = 3, Tues(today) = 2, diff = 1 (next day)
    // Mon = 1, Sun(today) = 7, diff = -6 (next day, next week)
    const weekdayDiff = weekdayStart - todayWeekdayNumber;
    return weekdayDiff === 1 || weekdayDiff === -6;
  });

  return filtered[0] ?? null;
}
